import { useState } from 'react'
import { Card, Segmented, Typography, Space, Avatar } from 'antd'
import {
  InstagramOutlined,
  FacebookOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  HeartOutlined,
  MessageOutlined,
  SendOutlined,
  BookOutlined,
} from '@ant-design/icons'

const { Text, Paragraph } = Typography

export type SocialPlatform = 'INSTAGRAM' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER' | 'YOUTUBE'

interface PlatformPreviewProps {
  videoUrl?: string
  imageUrl?: string
  thumbnailUrl?: string
  content: string
  selectedPlatforms: SocialPlatform[]
  accountName?: string
  accountHandle?: string
  profileImageUrl?: string
}

// Phone mockup wrapper
function PhoneMockup({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 260,
        height: 520,
        border: '8px solid #1a1a1a',
        borderRadius: 36,
        background: '#000',
        padding: 4,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}
    >
      {/* Notch */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: 28,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 24,
            background: '#1a1a1a',
            borderRadius: '0 0 16px 16px',
            zIndex: 10,
          }}
        />
        {children}
      </div>
    </div>
  )
}

// Instagram Reels Preview
function InstagramPreview({
  videoUrl,
  thumbnailUrl,
  content,
  accountName,
  profileImageUrl,
}: {
  videoUrl?: string
  thumbnailUrl?: string
  content: string
  accountName?: string
  profileImageUrl?: string
}) {
  return (
    <div style={{ height: '100%', background: '#000', position: 'relative' }}>
      {/* Video/Thumbnail Background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: thumbnailUrl
            ? `url(${thumbnailUrl}) center/cover`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      />

      {/* Overlay gradient */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.8))',
        }}
      />

      {/* Right side actions */}
      <div
        style={{
          position: 'absolute',
          right: 12,
          bottom: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <HeartOutlined style={{ color: 'white', fontSize: 24 }} />
          <div style={{ color: 'white', fontSize: 11 }}>1.2K</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <MessageOutlined style={{ color: 'white', fontSize: 22 }} />
          <div style={{ color: 'white', fontSize: 11 }}>45</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <SendOutlined style={{ color: 'white', fontSize: 22 }} />
        </div>
      </div>

      {/* Bottom content */}
      <div
        style={{
          position: 'absolute',
          left: 12,
          right: 48,
          bottom: 40,
        }}
      >
        <Space align="center" style={{ marginBottom: 8 }}>
          <Avatar size={28} src={profileImageUrl} style={{ background: '#1890ff' }}>
            {accountName?.[0] || 'P'}
          </Avatar>
          <Text style={{ color: 'white', fontWeight: 600, fontSize: 13 }}>
            {accountName || 'perfectamed'}
          </Text>
        </Space>
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{ color: 'white', fontSize: 12, margin: 0 }}
        >
          {content || 'Opis vašeg posta...'}
        </Paragraph>
      </div>

      {/* Reels indicator */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          right: 12,
        }}
      >
        <Text style={{ color: 'white', fontSize: 14, fontWeight: 600 }}>Reels</Text>
      </div>
    </div>
  )
}

// TikTok Preview
function TikTokPreview({
  videoUrl,
  thumbnailUrl,
  content,
  accountHandle,
  profileImageUrl,
}: {
  videoUrl?: string
  thumbnailUrl?: string
  content: string
  accountHandle?: string
  profileImageUrl?: string
}) {
  return (
    <div style={{ height: '100%', background: '#000', position: 'relative' }}>
      {/* Video/Thumbnail Background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: thumbnailUrl
            ? `url(${thumbnailUrl}) center/cover`
            : 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)',
        }}
      />

      {/* Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(transparent 50%, rgba(0,0,0,0.7))',
        }}
      />

      {/* Right side actions */}
      <div
        style={{
          position: 'absolute',
          right: 8,
          bottom: 120,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Avatar size={40} src={profileImageUrl} style={{ border: '2px solid white' }}>
          {accountHandle?.[0] || 'P'}
        </Avatar>
        <div style={{ textAlign: 'center' }}>
          <HeartOutlined style={{ color: 'white', fontSize: 28 }} />
          <div style={{ color: 'white', fontSize: 11 }}>12.5K</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <MessageOutlined style={{ color: 'white', fontSize: 26 }} />
          <div style={{ color: 'white', fontSize: 11 }}>234</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <BookOutlined style={{ color: 'white', fontSize: 26 }} />
          <div style={{ color: 'white', fontSize: 11 }}>1.2K</div>
        </div>
      </div>

      {/* Bottom content */}
      <div
        style={{
          position: 'absolute',
          left: 12,
          right: 56,
          bottom: 24,
        }}
      >
        <Text style={{ color: 'white', fontWeight: 700, fontSize: 14, display: 'block' }}>
          @{accountHandle || 'perfectamed'}
        </Text>
        <Paragraph
          ellipsis={{ rows: 3 }}
          style={{ color: 'white', fontSize: 12, margin: '4px 0 0' }}
        >
          {content || 'Opis vašeg posta...'}
        </Paragraph>
      </div>

      {/* TikTok logo */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 12,
        }}
      >
        <Text style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>TikTok</Text>
      </div>
    </div>
  )
}

// Twitter/X Preview
function TwitterPreview({
  thumbnailUrl,
  content,
  accountName,
  accountHandle,
  profileImageUrl,
}: {
  thumbnailUrl?: string
  content: string
  accountName?: string
  accountHandle?: string
  profileImageUrl?: string
}) {
  return (
    <div style={{ height: '100%', background: '#000', paddingTop: 32 }}>
      <div style={{ padding: 12, background: '#000' }}>
        {/* Tweet header */}
        <Space align="start">
          <Avatar size={36} src={profileImageUrl} style={{ background: '#1890ff' }}>
            {accountName?.[0] || 'P'}
          </Avatar>
          <div>
            <Text style={{ color: 'white', fontWeight: 600, fontSize: 13, display: 'block' }}>
              {accountName || 'PerfectaMed'}
            </Text>
            <Text style={{ color: '#71767b', fontSize: 12 }}>
              @{accountHandle || 'perfectamed'}
            </Text>
          </div>
        </Space>

        {/* Tweet content */}
        <Paragraph
          style={{ color: 'white', fontSize: 14, margin: '12px 0', lineHeight: 1.4 }}
          ellipsis={{ rows: 4 }}
        >
          {content.substring(0, 280) || 'Tekst vašeg tvita...'}
          {content.length > 280 && '...'}
        </Paragraph>

        {/* Video thumbnail */}
        {thumbnailUrl && (
          <div
            style={{
              width: '100%',
              aspectRatio: '16/9',
              borderRadius: 12,
              background: `url(${thumbnailUrl}) center/cover`,
              marginBottom: 12,
            }}
          />
        )}

        {/* Actions */}
        <Space size={24} style={{ marginTop: 8 }}>
          <MessageOutlined style={{ color: '#71767b', fontSize: 16 }} />
          <span style={{ color: '#71767b', fontSize: 12 }}>23</span>
          <HeartOutlined style={{ color: '#71767b', fontSize: 16 }} />
          <span style={{ color: '#71767b', fontSize: 12 }}>156</span>
        </Space>
      </div>
    </div>
  )
}

// YouTube Shorts Preview
function YouTubePreview({
  thumbnailUrl,
  content,
  accountName,
  profileImageUrl,
}: {
  thumbnailUrl?: string
  content: string
  accountName?: string
  profileImageUrl?: string
}) {
  // First line is title
  const title = content.split('\n')[0]?.substring(0, 100) || 'Naslov videa'

  return (
    <div style={{ height: '100%', background: '#0f0f0f', position: 'relative' }}>
      {/* Video thumbnail */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: thumbnailUrl
            ? `url(${thumbnailUrl}) center/cover`
            : 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
        }}
      />

      {/* Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.9))',
        }}
      />

      {/* Shorts badge */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 12,
          background: 'red',
          padding: '2px 8px',
          borderRadius: 4,
        }}
      >
        <Text style={{ color: 'white', fontSize: 11, fontWeight: 600 }}>Shorts</Text>
      </div>

      {/* Right actions */}
      <div
        style={{
          position: 'absolute',
          right: 12,
          bottom: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <HeartOutlined style={{ color: 'white', fontSize: 24 }} />
          <div style={{ color: 'white', fontSize: 11 }}>5.4K</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <MessageOutlined style={{ color: 'white', fontSize: 22 }} />
          <div style={{ color: 'white', fontSize: 11 }}>89</div>
        </div>
      </div>

      {/* Bottom content */}
      <div
        style={{
          position: 'absolute',
          left: 12,
          right: 48,
          bottom: 24,
        }}
      >
        <Space align="center" style={{ marginBottom: 4 }}>
          <Avatar size={24} src={profileImageUrl} style={{ background: '#ff0000' }}>
            {accountName?.[0] || 'P'}
          </Avatar>
          <Text style={{ color: 'white', fontSize: 12 }}>
            {accountName || 'PerfectaMed'}
          </Text>
        </Space>
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{ color: 'white', fontSize: 13, fontWeight: 500, margin: 0 }}
        >
          {title}
        </Paragraph>
      </div>
    </div>
  )
}

// Facebook Preview
function FacebookPreview({
  thumbnailUrl,
  content,
  accountName,
  profileImageUrl,
}: {
  thumbnailUrl?: string
  content: string
  accountName?: string
  profileImageUrl?: string
}) {
  return (
    <div style={{ height: '100%', background: '#18191a', paddingTop: 32 }}>
      <div style={{ padding: 12 }}>
        {/* Post header */}
        <Space align="center" style={{ marginBottom: 12 }}>
          <Avatar size={36} src={profileImageUrl} style={{ background: '#1877f2' }}>
            {accountName?.[0] || 'P'}
          </Avatar>
          <div>
            <Text style={{ color: 'white', fontWeight: 600, fontSize: 13, display: 'block' }}>
              {accountName || 'PerfectaMed'}
            </Text>
            <Text style={{ color: '#b0b3b8', fontSize: 11 }}>Sada</Text>
          </div>
        </Space>

        {/* Content */}
        <Paragraph
          ellipsis={{ rows: 3 }}
          style={{ color: '#e4e6eb', fontSize: 13, marginBottom: 12 }}
        >
          {content || 'Tekst vašeg posta...'}
        </Paragraph>

        {/* Video thumbnail */}
        {thumbnailUrl && (
          <div
            style={{
              width: '100%',
              aspectRatio: '9/16',
              maxHeight: 280,
              borderRadius: 8,
              background: `url(${thumbnailUrl}) center/cover`,
            }}
          />
        )}

        {/* Actions */}
        <Space size={24} style={{ marginTop: 12 }}>
          <Space>
            <HeartOutlined style={{ color: '#b0b3b8' }} />
            <Text style={{ color: '#b0b3b8', fontSize: 13 }}>Sviđa mi se</Text>
          </Space>
          <Space>
            <MessageOutlined style={{ color: '#b0b3b8' }} />
            <Text style={{ color: '#b0b3b8', fontSize: 13 }}>Komentar</Text>
          </Space>
        </Space>
      </div>
    </div>
  )
}

export function PlatformPreview({
  videoUrl,
  imageUrl,
  thumbnailUrl,
  content,
  selectedPlatforms,
  accountName = 'PerfectaMed',
  accountHandle = 'perfectamed',
  profileImageUrl,
}: PlatformPreviewProps) {
  // For images, use the image URL as the display background
  const displayUrl = imageUrl || thumbnailUrl
  const [activePlatform, setActivePlatform] = useState<SocialPlatform>(
    selectedPlatforms[0] || 'INSTAGRAM'
  )

  // Platform options for segmented control
  const platformOptions = selectedPlatforms.map((platform) => {
    const icons: Record<SocialPlatform, React.ReactNode> = {
      INSTAGRAM: <InstagramOutlined />,
      FACEBOOK: <FacebookOutlined />,
      TIKTOK: <span style={{ fontWeight: 'bold', fontSize: 12 }}>TT</span>,
      TWITTER: <TwitterOutlined />,
      YOUTUBE: <YoutubeOutlined />,
    }
    return {
      value: platform,
      label: icons[platform],
    }
  })

  // Render platform-specific preview
  const renderPreview = () => {
    const props = {
      videoUrl,
      thumbnailUrl: displayUrl,
      content,
      accountName,
      accountHandle,
      profileImageUrl,
    }

    switch (activePlatform) {
      case 'INSTAGRAM':
        return <InstagramPreview {...props} />
      case 'TIKTOK':
        return <TikTokPreview {...props} />
      case 'TWITTER':
        return <TwitterPreview {...props} />
      case 'YOUTUBE':
        return <YouTubePreview {...props} />
      case 'FACEBOOK':
        return <FacebookPreview {...props} />
      default:
        return <InstagramPreview {...props} />
    }
  }

  if (selectedPlatforms.length === 0) {
    return (
      <Card title="Pregled" size="small">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 400,
            color: '#8c8c8c',
          }}
        >
          <Text type="secondary">Izaberite platformu za pregled</Text>
        </div>
      </Card>
    )
  }

  return (
    <Card
      title="Pregled"
      size="small"
      extra={
        selectedPlatforms.length > 1 && (
          <Segmented
            size="small"
            options={platformOptions}
            value={activePlatform}
            onChange={(value) => setActivePlatform(value as SocialPlatform)}
          />
        )
      }
    >
      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
        <PhoneMockup>{renderPreview()}</PhoneMockup>
      </div>
    </Card>
  )
}

export default PlatformPreview
